import { QueryClient, QueryCache } from "@tanstack/react-query";
import { captureError } from "@/lib/sentry";
import { isNetworkFailure } from "@/lib/networkErrors";

const QUERY_RETRIES = 2;

// For a query that must not retry an answer from the server (the account
// check, the feature flags, the logged-out homepage), but should still ride
// out a dropped connection with the same retries as every other query.
export function retryNetworkFailures(failureCount: number, error: unknown): boolean {
  return failureCount < QUERY_RETRIES && isNetworkFailure(error);
}

// Shared client-side QueryClient defaults (#200). Previously the public app used
// a bare `new QueryClient()` — no retry, no global error handling.
//   - a small retry budget with exponential backoff rides out transient blips
//   - a short staleTime cuts redundant refetch churn on navigation
//   - a global query-error hook reports to Sentry so silently-failing reads are
//     visible (mutations keep the default retry: 0 — they may be non-idempotent)
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount) => failureCount < QUERY_RETRIES,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
        staleTime: 60 * 1000,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Browsers word a dropped connection differently and Sentry appends
        // the host, which split one failing query into several issues; group
        // those by the query instead.
        const fingerprint = isNetworkFailure(error) ? ["graphql-network", String(query.queryKey[0])] : undefined;
        captureError(error instanceof Error ? error : new Error(String(error)), {queryKey: query.queryKey}, fingerprint);
      },
    }),
  });
}
